const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5007';
const ADMIN_BASE = `${API_BASE}/api/admin`;

const ADMIN_TOKEN_KEY = 'vacation_admin_token';
const ADMIN_REFRESH_KEY = 'vacation_admin_refresh';

export function getAdminToken(): string | null {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}
export function setAdminTokens(token: string, refresh: string) {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
  localStorage.setItem(ADMIN_REFRESH_KEY, refresh);
}
export function clearAdminTokens() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_REFRESH_KEY);
}

function authHeaders(): Record<string, string> {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text();
    let msg = 'Error en la solicitud';
    try { msg = JSON.parse(body).message || msg; } catch { if (body) msg = body; }
    throw new Error(msg);
  }
  return res.json();
}

// =====================================================
// TIPOS
// =====================================================

export interface SuperAdminInfo {
  id: number;
  email: string;
  nombreCompleto: string;
}

export interface AdminAuthResponse {
  token: string;
  refreshToken: string;
  expiracion: string;
  usuario: SuperAdminInfo;
}

export interface PlanDto {
  codigo: string;
  nombre: string;
  precioUsdMes: number;
  maxEmpleados: number;
  activo: boolean;
}

export interface EmpresaResumen {
  id: number;
  nombre: string;
  estaActiva: boolean;
  plan: PlanDto;
  cantEmpleados: number;
  cantUsuarios: number;
  fechaAlta: string;
}

export interface EmpresaDetalle extends EmpresaResumen {
  emailAdmin: string;
}

// =====================================================
// AUTH
// =====================================================

export async function loginSuperAdmin(email: string, password: string): Promise<AdminAuthResponse> {
  const res = await fetch(`${ADMIN_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return handleResponse<AdminAuthResponse>(res);
}

// =====================================================
// PLANES
// =====================================================

export async function getPlanes(): Promise<PlanDto[]> {
  const res = await fetch(`${ADMIN_BASE}/planes`, { headers: authHeaders() });
  return handleResponse<PlanDto[]>(res);
}

export async function actualizarPlan(
  codigo: string,
  data: { precioUsdMes?: number; maxEmpleados?: number; activo?: boolean }
): Promise<PlanDto> {
  const res = await fetch(`${ADMIN_BASE}/planes/${codigo}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(data),
  });
  return handleResponse<PlanDto>(res);
}

// =====================================================
// EMPRESAS
// =====================================================

export async function getEmpresas(params: { busqueda?: string; soloActivas?: boolean }): Promise<EmpresaResumen[]> {
  const qs = new URLSearchParams();
  if (params.busqueda) qs.set('busqueda', params.busqueda);
  if (params.soloActivas !== undefined) qs.set('soloActivas', String(params.soloActivas));
  const res = await fetch(`${ADMIN_BASE}/empresas?${qs}`, { headers: authHeaders() });
  return handleResponse<EmpresaResumen[]>(res);
}

export async function getEmpresa(id: number): Promise<EmpresaDetalle> {
  const res = await fetch(`${ADMIN_BASE}/empresas/${id}`, { headers: authHeaders() });
  return handleResponse<EmpresaDetalle>(res);
}

export async function crearEmpresa(data: {
  nombre: string;
  planCodigo: string;
  emailAdmin: string;
  nombreAdmin: string;
}): Promise<{ empresa: EmpresaDetalle; passwordTemporal: string }> {
  const res = await fetch(`${ADMIN_BASE}/empresas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function cambiarEstadoEmpresa(id: number, estaActiva: boolean): Promise<void> {
  const res = await fetch(`${ADMIN_BASE}/empresas/${id}/estado`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ estaActiva }),
  });
  await handleResponse<unknown>(res);
}

export async function cambiarPlanEmpresa(id: number, nuevoPlanCodigo: string): Promise<void> {
  const res = await fetch(`${ADMIN_BASE}/empresas/${id}/plan`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ nuevoPlanCodigo }),
  });
  await handleResponse<unknown>(res);
}

export async function regenerarPassword(id: number): Promise<string> {
  const res = await fetch(`${ADMIN_BASE}/empresas/${id}/regenerar-password`, {
    method: 'POST',
    headers: authHeaders(),
  });
  const data = await handleResponse<{ passwordTemporal: string }>(res);
  return data.passwordTemporal;
}
